import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlogPost, BlogPostData } from "../utils/blogUtils";
import { useLoading } from "../contexts/LoadingContext";
import MermaidRenderer from "../components/MermaidRenderer";
import NotFound from "./NotFound";
import "./Blog.css";
import "./BlogPost.css";
import './mermaid.css';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setLoading: setGlobalLoading } = useLoading();

  useEffect(() => {
    let loadingSignaled = false;

    const fetchPost = async () => {
      setGlobalLoading(true);
      loadingSignaled = true;
      try {
        if (!slug) {
          throw new Error("No slug provided");
        }
        const data = await getBlogPost(slug);
        if (!data) {
          throw new Error("Post not found");
        }
        setPost(data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
        setError("Post not found");
      } finally {
        setLoading(false);
        if (loadingSignaled) {
          setGlobalLoading(false);
          loadingSignaled = false;
        }
      }
    };

    if (slug) {
      fetchPost();
    }

    return () => {
      if (loadingSignaled) {
        setGlobalLoading(false);
      }
    };
  }, [slug, setGlobalLoading]);

  if (!loading && (error || !post)) {
    return <NotFound />;
  }

  if (!post) {
    return null;
  }

  const handleTagClick = (tag: string) => {
    navigate(`/blog?tags=${encodeURIComponent(tag)}`);
  };

  const containerId = `blog-post-${slug}`;

  const content = (
    <div className="blog-post">
      <div className="container">
        <article className="post-card">
          <header className="post-header">
            <div className="post-meta">
              <span className="post-date">{post.date}</span>
            </div>
            <h1 className="post-title">{post.title}</h1>
            <p className="post-excerpt">{post.excerpt}</p>
            <div className="post-tags">
              {post.tags.map((tag) => (
                <button
                  key={tag}
                  className="tag tag-clickable"
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </header>

          <div className="post-body" id={containerId}>
            <div
              className="post-content-html"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
            <MermaidRenderer containerId={containerId} />
          </div>

          <footer className="post-footer">
          </footer>
        </article>
      </div>
    </div>
  );

  return content;
};

export default BlogPost;
